import React, { useEffect, useState, useRef, Suspense, lazy } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import InfoPage from "../InfoPage";
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { getAuthToken, decodeToken } from '../../utils/authHelpers';
import { authFetch } from '../../utils/authFetch';
import { getCloudinaryUrl } from '../../utils/cloudinary';
import { displayHandle as fmtHandle } from '../../utils/streamHelpers';
import MembershipBadge from '../../components/MembershipBadge';
import FounderBadge from '../../components/FounderBadge';
import ModalPortal from '../../components/Modal/ModalPortal';
import BoardComments from '../../components/BoardComments';
import BlurredGate from '../../components/BlurredGate';
import { showToast } from '../../components/Messaging/Toast';
import { usePersona } from '../../contexts/PersonaContext';
import IntroVideoPreview from '../../components/IntroVideoPreview';
import ApplyModal from '../../components/ApplyModal';
import './CollabBoard.sass';

// Lazy-loaded so the heavy mapbox-gl bundle only downloads when a user
// actually opens the Map view.
const CollabBoardMap = lazy(() => import('../../components/CollabBoardMap'));

const MAP_ENABLED = Boolean(process.env.REACT_APP_MAPBOX_TOKEN);

const SHARE_ALLOWLIST = ['jamie@postaway.com', 'jonathansgallegos@gmail.com', 'jtkraus@yahoo.com'];

const HeartIcon = ({ isLiked, size = 14 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: 'inline-block', verticalAlign: 'middle' }}
  >
    <path 
      fillRule="evenodd" 
      clipRule="evenodd" 
      d="M8.55284 3.00012C7.93598 3.00012 7.23841 3.06514 6.57209 3.29224C2.55494 4.60387 1.26341 8.894 2.39877 12.43L2.40354 12.4448L2.40877 12.4595C3.03435 14.2174 4.04226 15.8127 5.35336 17.1249L5.36091 17.1324L5.36862 17.1398C7.23782 18.9323 9.27254 20.4953 11.4756 21.8515L11.9934 22.1703L12.5147 21.8573C14.7226 20.5315 16.7964 18.9254 18.6432 17.1474L18.649 17.1419L18.6547 17.1362C19.9771 15.8215 20.9851 14.2144 21.6015 12.4549L21.6066 12.4402L21.6113 12.4253C22.7251 8.89703 21.4401 4.60176 17.4507 3.30948C16.7976 3.09221 16.1236 3.00012 15.4648 3.00012C13.9828 3.00011 12.8858 3.62064 12.0004 4.25309C11.1219 3.62545 10.0176 3.00012 8.55284 3.00012Z" 
      fill={isLiked ? '#dc1d08' : '#999'}
    />
  </svg>
);

const Card = ({
  img,
  fallbackImg,
  handle,
  onClick,
  likeCount = 0,
  isLiked = false,
  profileId,
  propertyId,
  onLikeToggle,
  collabDetails,
  collabStatus,
  introVideoUrl,
  introVideoPosterUrl,
  introVideoPublicId,
}) => {
  const [currentSrc, setCurrentSrc] = useState(img);
  const [hasTriedFallback, setHasTriedFallback] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  
  // Check if this is a paid partnership
  const isPaidPartnership = collabDetails?.compensation?.type === 'paid_partnership';
  
  const handleImageError = () => {
    // If we haven't tried the fallback yet and it's different from the current source
    if (!hasTriedFallback && fallbackImg && fallbackImg !== currentSrc) {
      setHasTriedFallback(true);
      setCurrentSrc(fallbackImg);
      return;
    }
    // Both URLs failed, hide the image
    setImageFailed(true);
  };
  
  const handleHeartClick = async (e) => {
    e.stopPropagation(); // Prevent card click
    if (!onLikeToggle) return;
    
    const token = getAuthToken();
    if (!token) {
      alert('Please log in to like collab opportunities');
      return;
    }
    
    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      const payload = profileId ? { profile_id: profileId } : { property_id: propertyId };
      
      const res = await authFetch(`${base}/api/collab/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      if (res.ok && data.ok) {
        onLikeToggle(data.liked, data.like_count);
      } else {
        console.error('Failed to toggle like');
      }
    } catch (err) {
      // Don't log auth errors since authFetch handles logout
      if (!err.message?.includes('Unauthorized') && !err.message?.includes('expired')) {
        console.error('Like toggle error:', err);
      }
    }
  };

  return (
    <div 
      className="collab-board-card"
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
    >
      {currentSrc && !imageFailed ? (
        <div className="collab-board-image-wrapper">
          {introVideoUrl ? (
            <IntroVideoPreview
              mode="card"
              posterUrl={introVideoPosterUrl}
              publicId={introVideoPublicId}
              fullUrl={introVideoUrl}
              fallbackImageUrl={currentSrc}
              alt={handle}
            />
          ) : (
            <img
              src={currentSrc}
              alt={handle}
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
              onError={handleImageError}
              onMouseEnter={(e) => { if (onClick) { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.12)'; }}}
              onMouseLeave={(e) => { if (onClick) { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.08)'; }}}
              style={collabStatus ? { filter: 'blur(1.5px) brightness(0.85)', transition: 'filter 0.3s' } : undefined}
            />
          )}
          {collabStatus && (
            <div className="collab-status-overlay">
              <span className="collab-status-text">{collabStatus === 'pending' ? 'WAITLIST' : 'COMPLETED'}</span>
            </div>
          )}
          {isPaidPartnership && (
            <div className="collab-paid-indicator">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="9" fill="#dc1d08"/>
                <text x="10" y="14" textAnchor="middle" fill="white" fontSize="10" fontFamily="Arial, sans-serif" fontWeight="bold">$</text>
              </svg>
            </div>
          )}
        </div>
      ) : null}
      <div className="collab-board-card-name">
        <span>{handle}</span>
        <span 
          className="collab-board-card-like"
          onClick={handleHeartClick}
        >
          <HeartIcon isLiked={isLiked} size={14} />
          {likeCount > 0 && (
            <span style={{ 
              fontSize: '12px', 
              color: isLiked ? '#dc1d08' : '#999',
              fontFamily: "'Neue Einstellung', 'Poppins', sans-serif"
            }}>
              {likeCount}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

const NavigationButton = ({ direction, onClick, show }) => {
  if (!show) return null;
  
  return (
    <motion.button
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className={`collab-board-nav-button ${direction === 'left' ? 'nav-left' : 'nav-right'}`}
    >
      <svg 
        width="16" 
        height="16" 
        viewBox="0 0 16 16" 
        fill="none" 
        style={{ transform: direction === 'left' ? 'rotate(180deg)' : 'none' }}
      >
        <path 
          d="M6 4L10 8L6 12" 
          stroke="white" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        />
      </svg>
    </motion.button>
  );
};

const Modal = ({ isOpen, onClose, title, content, image, planId, planName, messageUserId, propertyId, propertySlug, profileId, host_profile_id, isOwner, onDelete, onEdit, onStatusChange, onApply, handle, airbnbUrl, vrboUrl, directBookingUrl, founder, collabDetails, collabStatus, squareFootage, locationCity, locationState, locationCountry, focusCommentId }) => {
  // Helper function to format structured collab details
  const renderCollabDetails = (details) => {
    if (!details) return null;
    
    const sections = [];
    
    // Dates icon
    const DatesIcon = () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc1d08" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/><path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/><path d="M8 18h.01"/><path d="M12 18h.01"/><path d="M16 18h.01"/>
      </svg>
    );
    
    // Deliverables icon
    const DeliverablesIcon = () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc1d08" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
      </svg>
    );
    
    // Stay icon
    const StayIcon = () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc1d08" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/><path d="m9 16 2 2 4-4"/>
      </svg>
    );
    
    // Compensation icon
    const CompensationIcon = () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc1d08" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/><path d="M12 18V6"/>
      </svg>
    );
    
    // Notes icon
    const NotesIcon = () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc1d08" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13.4 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7.4"/><path d="M2 6h4"/><path d="M2 10h4"/><path d="M2 14h4"/><path d="M2 18h4"/><path d="M21.378 5.626a1 1 0 1 0-3.004-3.004l-5.01 5.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"/>
      </svg>
    );
    
    // Dates
    if (details.dates) {
      if (details.dates.type === 'specific' && details.dates.start && details.dates.end) {
        const startDate = new Date(details.dates.start).toLocaleDateString();
        const endDate = new Date(details.dates.end).toLocaleDateString();
        sections.push(
          <div key="dates" className="collab-detail-item">
            <DatesIcon />
            <div><strong>Dates:</strong> {startDate} to {endDate}</div>
          </div>
        );
      } else if (details.dates.type === 'flexible') {
        sections.push(
          <div key="dates" className="collab-detail-item">
            <DatesIcon />
            <div><strong>Dates:</strong> Flexible</div>
          </div>
        );
      }
    }
    
    // Compensation
    if (details.compensation && details.compensation.type) {
      let compText = '';
      if (details.compensation.type === 'paid_partnership') {
        compText = 'Paid Partnership';
        if (details.compensation.budgetMin || details.compensation.budgetMax) {
          const min = details.compensation.budgetMin ? `$${details.compensation.budgetMin}` : '';
          const max = details.compensation.budgetMax ? `$${details.compensation.budgetMax}` : '';
          compText += min || max ? ` (${min}${min && max ? ' - ' : ''}${max})` : '';
        }
      } else if (details.compensation.type === 'hosted_stay') {
        compText = 'Hosted Stay';
      } else if (details.compensation.type === 'negotiable') {
        compText = 'Negotiable';
      }
      if (compText) {
        sections.push(
          <div key="compensation" className="collab-detail-item">
            <CompensationIcon />
            <div><strong>Compensation:</strong> {compText}</div>
          </div>
        );
      }
    }
    
    // Deliverables
    if (details.deliverables) {
      if (details.deliverables.type === 'specific' && details.deliverables.items && details.deliverables.items.length > 0) {
        sections.push(
          <div key="deliverables" className="collab-detail-item">
            <DeliverablesIcon />
            <div><strong>Deliverables:</strong> {details.deliverables.items.join(', ')}</div>
          </div>
        );
      } else if (details.deliverables.type === 'flexible') {
        sections.push(
          <div key="deliverables" className="collab-detail-item">
            <DeliverablesIcon />
            <div><strong>Deliverables:</strong> Flexible</div>
          </div>
        );
      }
    }
    
    // Stay details (only guest capacity, nights removed)
    if (details.stay && details.stay.guestCapacity) {
      sections.push(
        <div key="stay" className="collab-detail-item">
          <StayIcon />
          <div><strong>Stay:</strong> Max {details.stay.guestCapacity} guests</div>
        </div>
      );
    }
    
    // Additional notes
    if (details.additionalNotes) {
      sections.push(
        <div key="notes" className="collab-detail-item">
          <NotesIcon />
          <div><strong>Additional Notes:</strong> {details.additionalNotes}</div>
        </div>
      );
    }
    
    return sections.length > 0 ? <div className="collab-details-section">{sections}</div> : null;
  };
  const [isPortrait, setIsPortrait] = useState(null);

  // Reset state when modal closes or image changes
  useEffect(() => {
    if (!isOpen || !image) {
      setIsPortrait(null);
    }
  }, [isOpen, image]);

  if (!title) return null;

  // Handle image load to detect aspect ratio
  const handleImageLoad = (e) => {
    const img = e.target;
    if (img.naturalWidth && img.naturalHeight) {
      setIsPortrait(img.naturalWidth < img.naturalHeight);
    }
  };

  // Handle image error - default to landscape behavior
  const handleImageError = (e) => {
    setIsPortrait(false);
  };

  // Only show blurred sides for creator portrait images (creators don't have propertyId)
  const shouldShowBlur = !propertyId && isPortrait === true;
  
  // Check if this is a paid partnership
  const isPaidPartnership = collabDetails?.compensation?.type === 'paid_partnership';

  const viewerEmail = (() => {
    try { return (localStorage.getItem('postaway_email') || '').toLowerCase(); } catch (_) { return ''; }
  })();
  const canSharePublicLink = SHARE_ALLOWLIST.includes(viewerEmail);
  const publicSharePath = propertyId
    ? `/c/p/${encodeURIComponent(propertySlug || propertyId)}`
    : profileId
    ? `/c/u/${encodeURIComponent((handle || '').trim() || profileId)}`
    : '';

  const handleCopyPublicLink = async () => {
    if (!publicSharePath) return;
    const url = `${window.location.origin}${publicSharePath}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement('input');
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      showToast('Public collab link copied', 'success', null, 3000);
    } catch (_) {
      showToast('Unable to copy link. Please try again.', 'error', null, 4000);
    }
  };

  // Listing location (for host/property collab modals only) - pulled from the property listing fields
  const listingLocation = (() => {
    if (!propertyId) return '';
    const parts = [];
    if (locationCity) parts.push(locationCity);
    if (locationState) parts.push(locationState);
    if (locationCountry && locationCountry !== 'United States') parts.push(locationCountry);
    return parts.join(', ') || '';
  })();
  
  return (
    <ModalPortal isOpen={isOpen} onClose={onClose} maxWidth="600px" maxHeight="80vh">
        {image && (
          <div className="collab-modal-header" style={{ 
            justifyContent: shouldShowBlur ? 'center' : 'stretch',
            position: 'relative'
          }}>
            {/* Blurred background layer - only for creator portrait images */}
            {shouldShowBlur && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                filter: 'blur(20px)',
                transform: 'scale(1.1)',
                zIndex: 0
              }} />
            )}
            
            {/* Main image */}
            <img 
              src={image} 
              alt={title}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{
                position: 'relative',
                zIndex: 1,
                width: shouldShowBlur ? 'auto' : '100%',
                height: shouldShowBlur ? '100%' : '100%',
                maxWidth: shouldShowBlur ? '100%' : 'none',
                maxHeight: shouldShowBlur ? '100%' : 'none',
                objectFit: shouldShowBlur ? 'contain' : 'cover'
              }}
            />
            {isPaidPartnership && (
              <div className="collab-paid-indicator collab-paid-indicator-modal">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="11" fill="#dc1d08"/>
                  <text x="12" y="16.5" textAnchor="middle" fill="white" fontSize="12" fontFamily="Arial, sans-serif" fontWeight="bold">$</text>
                </svg>
              </div>
            )}
          </div>
        )}
        
        <div className="collab-modal-content">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
              <h2 className="collab-modal-title" style={propertyId && listingLocation ? { marginBottom: '8px' } : undefined}>
                {title}
              </h2>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FounderBadge isFounder={founder} />
              <MembershipBadge planId={planId} planName={planName} userType={propertyId ? 'host' : 'creator'} />
              {canSharePublicLink && publicSharePath && (
                <button
                  type="button"
                  className="copy-link-pill"
                  aria-label="Copy public collab link"
                  title="Copy public collab link"
                  onClick={handleCopyPublicLink}
                >
                  <svg
                    className="copy-link-pill__icon"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                  <span className="copy-link-pill__label">Share</span>
                </button>
              )}
            </div>
          </div>

          {propertyId && listingLocation && (
            <p className="collab-modal-location">{listingLocation}</p>
          )}
          
          <div className="collab-modal-description">
            {content || 'No collaboration opportunity details provided yet.'}
          </div>
          
          {/* Display structured collab details */}
          {collabDetails && renderCollabDetails(collabDetails)}
          
          {/* Display square footage if available */}
          {squareFootage && (
            <div className="collab-detail-item">
              <strong>Square Footage:</strong> {squareFootage.toLocaleString()} sq ft
            </div>
          )}
          
          <div className="collab-modal-actions">
            <div className="collab-modal-buttons">
            {isOwner && (
              <>
                <button
                  className="button-edit"
                  onClick={onEdit}
                  style={{
                    padding: '10px 20px',
                    border: '2px solid #0f084b',
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    color: '#0f084b',
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#0f084b';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.color = '#0f084b';
                  }}
                >
                  Edit
                </button>
                <button
                  className="button-delete"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#dc1d08';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.color = '#dc1d08';
                  }}
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this collaboration opportunity?')) {
                      onDelete();
                    }
                  }}
                >
                  Delete
                </button>
                <button
                  className={`button-status${collabStatus === 'pending' ? ' button-status-active' : ''}`}
                  onClick={() => onStatusChange(collabStatus === 'pending' ? null : 'pending')}
                >
                  {collabStatus === 'pending' ? '✓ Waitlist' : 'Mark as Waitlist'}
                </button>
                <button
                  className={`button-status button-status-complete${collabStatus === 'completed' ? ' button-status-active' : ''}`}
                  onClick={() => onStatusChange(collabStatus === 'completed' ? null : 'completed')}
                  title="This ends the board listing only. Looking to complete a collab with a creator? Do that in your Collab Tracker."
                >
                  {collabStatus === 'completed' ? '✓ Post Closed' : 'Close This Post'}
                </button>
              </>
            )}
            {!isOwner && (
              <button
                className="button-request"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#b51706';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 29, 8, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#dc1d08';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onClick={() => {
                  // Open the new ApplyModal instead of jumping into a DM.
                  // The DM no longer opens at this step — the host has to
                  // approve from their /collabs Pending tab first.
                  if (!messageUserId) {
                    window.location.href = '/messages';
                    return;
                  }
                  const ctx = {
                    recipientUserId: messageUserId,
                    recipientHandle: handle,
                    title,
                    collabDetails,
                  };
                  if (propertyId) {
                    ctx.targetKind = 'property';
                    ctx.targetId = propertyId;
                  } else if (profileId) {
                    ctx.targetKind = 'creator_profile';
                    ctx.targetId = profileId;
                  } else if (host_profile_id) {
                    ctx.targetKind = 'host_profile';
                    ctx.targetId = host_profile_id;
                  }
                  if (typeof onApply === 'function') {
                    onApply(ctx);
                  }
                }}
              >
                Apply to Collab
              </button>
            )}
            
            {(profileId || host_profile_id || propertyId || handle) && (
              <button
                className="button-profile"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#0f084b';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(15, 8, 75, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#0f084b';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onClick={() => {
                  const params = new URLSearchParams();
                  if (profileId && !propertyId) {
                    // Creator with ID
                    params.set('creator_id', profileId);
                  } else if (host_profile_id) {
                    // Host with ID (preferred)
                    params.set('host_id', host_profile_id);
                    if (propertyId) {
                      params.set('property_id', propertyId);
                    }
                  } else if (propertyId) {
                    // Host with property_id only (fallback)
                    params.set('property_id', propertyId);
                  } else if (handle) {
                    // Fallback to handle
                    params.set('handle', handle);
                  }
                  window.location.href = `/profile?${params.toString()}`;
                }}
              >
                View Profile
              </button>
            )}
            </div>
            
            {/* Booking Links - Icons on the right */}
            {(airbnbUrl || vrboUrl || directBookingUrl) && (
              <div className="collab-modal-booking-links">
                {directBookingUrl && (
                  <Tippy content="Book directly with the host and save">
                  <a
                    href={directBookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <img src="/circle-p-logo.png" alt="Direct" />
                  </a>
                  </Tippy>
                )}
                {vrboUrl && (
                  <a
                    href={vrboUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <img src="/vrbo logo.png" alt="VRBO" />
                  </a>
                )}
                {airbnbUrl && (
                  <a
                    href={airbnbUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <img src="/airbnb_logo.png" alt="Airbnb" />
                  </a>
                )}
              </div>
            )}
          </div>
          
          {/* Comments Section */}
          <BoardComments
            target={propertyId 
              ? { kind: 'collab_property', id: propertyId }
              : { kind: 'collab_creator', id: profileId }
            }
            postOwnerUserId={messageUserId}
            isOpen={isOpen}
            focusCommentId={focusCommentId}
          />
        </div>
    </ModalPortal>
  );
};

const CollabBoard = () => {
  const { activePersona } = usePersona();
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [email, setEmail] = useState('');
  const [data, setData] = useState({ hosts: [], creators: [] });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedHost, setSelectedHost] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [hostStartIndex, setHostStartIndex] = useState(0);
  const [creatorStartIndex, setCreatorStartIndex] = useState(0);
  const [hostDirection, setHostDirection] = useState(1); // -1 for back, 1 for forward
  const [creatorDirection, setCreatorDirection] = useState(1); // -1 for back, 1 for forward
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [postText, setPostText] = useState('');
  const [postRole, setPostRole] = useState(null); // 'host' | 'creator'
  const [postPropertyId, setPostPropertyId] = useState('');
  const [userRoles, setUserRoles] = useState([]);
  const [userProperties, setUserProperties] = useState([]);
  const [focusCommentId, setFocusCommentId] = useState(null);

  // Application gate (apply -> host approves -> chat opens). When the user
  // clicks "Apply to Collab" we stash the context and show <ApplyModal/>.
  // We deliberately do NOT navigate to /messages from here — that's what
  // caused the host-DM-spam problem this whole feature is fixing.
  const [applyContext, setApplyContext] = useState(null);

  // Membership tier for gating
  const [membershipTier, setMembershipTier] = useState(null);
  
  // Ref to track if we've handled deep-link for profileId/propertyId/hostId (prevent re-open on modal close)
  const handledDeepLinkRef = useRef(false);
  
  // View All toggle state
  const [showAllHosts, setShowAllHosts] = useState(false);
  const [showAllCreators, setShowAllCreators] = useState(false);

  // Hosts grid vs map view (map gated on a configured Mapbox token).
  const [searchParams, setSearchParams] = useSearchParams();
  const [hostsView, setHostsView] = useState(() =>
    (MAP_ENABLED && searchParams.get('view') === 'map') ? 'map' : 'grid'
  );
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Post expiration
  const [postExpirationType, setPostExpirationType] = useState('indefinitely'); // 'date' | 'indefinitely'
  const [postExpirationDate, setPostExpirationDate] = useState('');
  
  // Enhanced collab fields state
  const [datesType, setDatesType] = useState('flexible'); // 'specific' | 'flexible'
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [compensationType, setCompensationType] = useState(''); // 'paid_partnership' | 'hosted_stay' | 'negotiable'
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [deliverablesType, setDeliverablesType] = useState('flexible'); // 'specific' | 'flexible'
  const [deliverables, setDeliverables] = useState([]); // Array of selected deliverables
  const [guestCapacity, setGuestCapacity] = useState('');
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [primaryGoal, setPrimaryGoal] = useState(''); // 'increase_bookings' | 'increase_following' | 'update_photos'
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [location, setLocation] = useState('');
  
  // Track property views when modal opens with a property
  useEffect(() => {
    if (modalOpen && selectedHost?.property_id && selectedHost?.user_id !== currentUserId) {
      // Only track if it's not the owner viewing their own property
      const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      fetch(`${apiBase}/api/properties/track-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id: selectedHost.property_id })
      }).catch(err => console.error('Failed to track property view:', err));
    }
  }, [modalOpen, selectedHost?.property_id, selectedHost?.user_id, currentUserId]);

  // Track creator profile views when modal opens with a creator
  useEffect(() => {
    if (modalOpen && selectedHost?.profile_id && !selectedHost?.property_id && selectedHost?.user_id !== currentUserId) {
      // Only track if it's a creator (has profile_id but no property_id) and not the owner
      const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      fetch(`${apiBase}/api/creator/track-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ creator_profile_id: selectedHost.profile_id })
      }).catch(err => console.error('Failed to track creator profile view:', err));
    }
  }, [modalOpen, selectedHost?.profile_id, selectedHost?.property_id, selectedHost?.user_id, currentUserId]);

  // Extract Instagram handle from URL
  const extractInstagramHandle = (url) => {
    if (!url) return null;
    try {
      // Handle various Instagram URL formats
      // Examples: 
      // https://www.instagram.com/username
      // https://instagram.com/username/
      // https://www.instagram.com/username?utm_source=...
      // https://www.instagram.com/username&source=google
      
      // Remove protocol and www
      let cleaned = url.replace(/^https?:\/\/(www\.)?/, '');
      
      // Check if it's an Instagram URL
      if (!cleaned.startsWith('instagram.com/')) {
        // Try to extract from the full URL if it contains instagram.com
        const match = url.match(/instagram\.com\/([^/?&#]+)/);
        return match ? `@${match[1]}` : null;
      }
      
      // Remove instagram.com/
      cleaned = cleaned.replace('instagram.com/', '');
      
      // Get the username (everything before /, ?, &, or #)
      const username = cleaned.split(/[/?&#]/)[0];
      
      // Return with @ prefix if we have a valid username
      return username ? `@${username}` : null;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let e = params.get('email') || '';
    if (!e) {
      try { e = localStorage.getItem('postaway_email') || ''; } catch (_) {}
    }
    setEmail(e);
    if (!e) {
      console.warn('CollabBoard: missing email; append ?email=you@example.com to URL');
    }
    
    // Get current user ID from token
    const token = getAuthToken();
    if (token) {
      const decoded = decodeToken(token);
      if (decoded && decoded.userId) {
        setCurrentUserId(decoded.userId);
      }
    }
    
    (async () => {
      try {
        const res = await fetch('/api/access/subscription', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: e }) });
        const data = await res.json();
        if (res.ok && data.ok && data.active && (data.roles || []).some(r => r === 'creator' || r === 'host')) {
          setAuthorized(true);
          setUserRoles(data.roles || []);
          setMembershipTier(data.membershipTier || 'all_access');
        } else if (res.ok && data.ok && data.active && data.membershipTier === 'basic') {
          setAuthorized('basic');
          setUserRoles(data.roles || []);
          setMembershipTier('basic');
        } else {
          navigate('/');
        }
      } catch (err) {
        // Redirect to home page on error
        navigate('/');
      }
    })();
  }, [navigate]);

  // Function to fetch collab board data
  const fetchCollabBoard = async (search = '') => {
    if (authorized !== true || !email) return;
    try {
      const res = await fetch('/api/collab/board', { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify({ email, search }) 
      });
      const json = await res.json();
      if (!res.ok) {
        console.error('CollabBoard fetch error', res.status, json);
      }
      if (res.ok && json.ok) {
        setData({ hosts: json.hosts || [], creators: json.creators || [] });
        // Reset carousel indices when search changes
        setHostStartIndex(0);
        setCreatorStartIndex(0);
      }
    } catch (_) {}
  };

  // Keep the ?view= param in sync so the chosen Hosts view is shareable.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (hostsView === 'map') next.set('view', 'map');
    else next.delete('view');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [hostsView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open the existing host detail modal from a map pin. Mirrors the grid
  // card's onClick so the apply flow is identical in both views.
  const openHostModalFromProperty = (property) => {
    const rawUrl = property.property_image_url;
    const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
    let resolved = rawUrl || '';
    if (resolved.startsWith('/') && isLocal && resolved.startsWith('/uploads/')) {
      resolved = `http://localhost:4000${resolved}`;
    }
    setSelectedHost({
      ...property,
      description: property.collab_opportunity || property.description,
      image: resolved,
      plan_id: property.plan_id,
      plan_name: property.plan_name,
      handle: property.handle,
      user_id: property.user_id,
      airbnb_url: property.airbnb_url,
      vrbo_url: property.vrbo_url,
      direct_booking_url: property.direct_booking_url,
      founder: property.founder,
      collab_details: property.collab_details,
      collab_status: property.collab_status,
      square_footage: property.square_footage,
    });
    setModalOpen(true);
  };

  // Open the host modal for a creator pin. Mirrors the creator grid card's
  // onClick (creators reuse the same modal, keyed off profile_id).
  const openCreatorModalFromProfile = (c) => {
    const rawUrl = c.avatar_asset_url || c.avatar_url;
    const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
    let resolved = rawUrl || '';
    if (resolved.startsWith('/') && isLocal && resolved.startsWith('/uploads/')) {
      resolved = `http://localhost:4000${resolved}`;
    }
    const displayHandle = c.handle ? fmtHandle(c.handle) : (c.first_name || '@creator');
    setSelectedHost({
      ...c,
      property_name: displayHandle,
      description: c.collab_request,
      image: resolved,
      plan_id: c.plan_id,
      plan_name: c.plan_name,
      handle: c.handle,
      user_id: c.user_id,
      profile_id: c.profile_id,
      founder: c.founder,
      collab_details: c.collab_details,
      collab_status: c.collab_status,
    });
    setModalOpen(true);
  };

  // Handle collab status change (API still uses pending; UI shows waitlist / completed)
  const handleStatusChange = async (newStatus) => {
    if (!selectedHost) return;
    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      const isProperty = Boolean(selectedHost.property_id);
      const payload = {
        type: isProperty ? 'property' : 'profile',
        propertyId: isProperty ? (selectedHost.property_id || selectedHost.id) : undefined,
        profileId: !isProperty ? (selectedHost.profile_id || selectedHost.id) : undefined,
        status: newStatus
      };

      const res = await authFetch(`${base}/api/collab/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await res.json();
      if (res.ok && result.ok) {
        // Update local data to reflect the new status
        setData(prevData => {
          const updateItem = (item) => {
            if (isProperty && item.property_id === selectedHost.property_id) {
              return { ...item, collab_status: newStatus };
            }
            if (!isProperty && item.profile_id === selectedHost.profile_id) {
              return { ...item, collab_status: newStatus };
            }
            return item;
          };
          return {
            hosts: prevData.hosts.map(updateItem),
            creators: prevData.creators.map(updateItem)
          };
        });
        // Update the selected host in the modal
        setSelectedHost(prev => prev ? { ...prev, collab_status: newStatus } : prev);
      } else {
        alert('Failed to update status');
      }
    } catch (err) {
      if (!err.message?.includes('Unauthorized') && !err.message?.includes('expired')) {
        console.error('Status update error:', err);
        alert('Failed to update status');
      }
    }
  };

  // Handle like toggle
  const handleLikeToggle = (isLiked, likeCount, profileId, propertyId) => {
    setData(prevData => {
      const updateItem = (item) => {
        if (profileId && item.profile_id === profileId) {
          return { ...item, is_liked: isLiked, like_count: likeCount };
        }
        if (propertyId && item.property_id === propertyId) {
          return { ...item, is_liked: isLiked, like_count: likeCount };
        }
        return item;
      };
      
      return {
        hosts: prevData.hosts.map(updateItem),
        creators: prevData.creators.map(updateItem)
      };
    });
  };

  // Handle search
  const handleSearch = () => {
    setSearchTerm(searchInput);
    fetchCollabBoard(searchInput);
  };

  // Handle Enter key press in search input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    fetchCollabBoard('');
  };

  // Initial load when authorized
  useEffect(() => {
    if (authorized === true && email) {
      fetchCollabBoard('');
    }
  }, [authorized, email]);

  // Handle commentId deep-linking from URL params
  useEffect(() => {
    const handleCommentDeepLink = async () => {
      const params = new URLSearchParams(window.location.search);
      const commentId = params.get('commentId');
      
      if (!commentId || data.hosts.length === 0 && data.creators.length === 0) {
        return;
      }

      setFocusCommentId(commentId);

      try {
        const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
        const res = await fetch(`${base}/api/board/comments/resolve?commentId=${encodeURIComponent(commentId)}`);
        const result = await res.json();
        
        if (!result.ok || !result.target) {
          return;
        }

        const { target } = result;
        
        // Find matching card and open modal
        if (target.kind === 'collab_property') {
          const property = data.hosts.find(h => h.property_id === target.id);
          if (property) {
            const rawUrl = property.property_image_url;
            const isLocal = window.location.hostname === 'localhost';
            let resolved = rawUrl || '';
            if (resolved.startsWith('/') && isLocal) {
              resolved = resolved.startsWith('/uploads/') ? `http://localhost:4000${resolved}` : resolved;
            }
            
            setSelectedHost({ 
              ...property, 
              description: property.collab_opportunity || property.description,
              image: resolved,
              plan_id: property.plan_id,
              plan_name: property.plan_name,
              handle: property.handle,
              user_id: property.user_id,
              airbnb_url: property.airbnb_url,
              vrbo_url: property.vrbo_url,
              direct_booking_url: property.direct_booking_url,
              founder: property.founder,
              collab_details: property.collab_details,
              collab_status: property.collab_status,
              square_footage: property.square_footage
            });
            setModalOpen(true);
          }
        } else if (target.kind === 'collab_creator') {
          const creator = data.creators.find(c => c.profile_id === target.id);
          if (creator) {
            const rawUrl = creator.avatar_asset_url || creator.avatar_url;
            const isLocal = window.location.hostname === 'localhost';
            let resolved = rawUrl || '';
            if (resolved.startsWith('/') && isLocal) {
              resolved = resolved.startsWith('/uploads/') ? `http://localhost:4000${resolved}` : resolved;
            }
            
            const instagramHandle = creator.instagram_url?.match(/instagram\.com\/([^/?&#]+)/)?.[1];
            const displayHandle = creator.handle ? fmtHandle(creator.handle) : (instagramHandle ? `@${instagramHandle}` : (creator.first_name || '@creator'));
            
            setSelectedHost({ 
              ...creator,
              property_name: displayHandle,
              description: creator.collab_request,
              image: resolved,
              plan_id: creator.plan_id,
              plan_name: creator.plan_name,
              handle: creator.handle,
              user_id: creator.user_id,
              profile_id: creator.profile_id,
              founder: creator.founder,
              collab_details: creator.collab_details,
              collab_status: creator.collab_status
            });
            setModalOpen(true);
          }
        }
      } catch (err) {
        console.error('Error resolving comment deep link:', err);
      }
    };

    handleCommentDeepLink();
  }, [data.hosts, data.creators]);

  // Handle profileId/propertyId/hostId deep-linking from URL params (for "View Collab Post" button)
  useEffect(() => {
    // Guard: only run once per page load, and only when data is available
    if (handledDeepLinkRef.current) return;
    if (data.hosts.length === 0 && data.creators.length === 0) return;

    const params = new URLSearchParams(window.location.search);
    const profileId = params.get('profileId');
    const propertyId = params.get('propertyId');
    const hostId = params.get('hostId');

    if (!profileId && !propertyId && !hostId) return;

    handledDeepLinkRef.current = true;

    if (propertyId || hostId) {
      // Find matching host/property and open modal
      const property = propertyId
        ? data.hosts.find(h => String(h.property_id) === String(propertyId))
        : data.hosts.find(h => String(h.host_profile_id) === String(hostId));
      if (property) {
        const rawUrl = property.property_image_url;
        const isLocal = window.location.hostname === 'localhost';
        let resolved = rawUrl || '';
        if (resolved.startsWith('/') && isLocal) {
          resolved = resolved.startsWith('/uploads/') ? `http://localhost:4000${resolved}` : resolved;
        }

        setSelectedHost({
          ...property,
          description: property.collab_opportunity || property.description,
          image: resolved,
          plan_id: property.plan_id,
          plan_name: property.plan_name,
          handle: property.handle,
          user_id: property.user_id,
          airbnb_url: property.airbnb_url,
          vrbo_url: property.vrbo_url,
          direct_booking_url: property.direct_booking_url,
          founder: property.founder,
          collab_details: property.collab_details,
          collab_status: property.collab_status,
          square_footage: property.square_footage
        });
        setModalOpen(true);
      }
    } else if (profileId) {
      // Find matching creator and open modal
      const creator = data.creators.find(c => String(c.profile_id) === String(profileId));
      if (creator) {
        const rawUrl = creator.avatar_asset_url || creator.avatar_url;
        const isLocal = window.location.hostname === 'localhost';
        let resolved = rawUrl || '';
        if (resolved.startsWith('/') && isLocal) {
          resolved = resolved.startsWith('/uploads/') ? `http://localhost:4000${resolved}` : resolved;
        }

        const instagramHandle = creator.instagram_url?.match(/instagram\.com\/([^/?&#]+)/)?.[1];
        const displayHandle = creator.handle ? fmtHandle(creator.handle) : (instagramHandle ? `@${instagramHandle}` : (creator.first_name || '@creator'));

        setSelectedHost({
          ...creator,
          property_name: displayHandle,
          description: creator.collab_request,
          image: resolved,
          plan_id: creator.plan_id,
          plan_name: creator.plan_name,
          handle: creator.handle,
          user_id: creator.user_id,
          profile_id: creator.profile_id,
          founder: creator.founder,
          collab_details: creator.collab_details,
          collab_status: creator.collab_status
        });
        setModalOpen(true);
      }
    }
  }, [data.hosts, data.creators]);

  // Handle editProperty/editCreator deep-linking from Collabs tracker "Edit Post" button
  useEffect(() => {
    if (data.hosts.length === 0 && data.creators.length === 0) return;

    const params = new URLSearchParams(window.location.search);
    const editPropertyId = params.get('editProperty');
    const editCreatorId = params.get('editCreator');

    if (!editPropertyId && !editCreatorId) return;

    // Clear the query params so this doesn't re-trigger
    window.history.replaceState({}, '', window.location.pathname);

    if (editPropertyId) {
      const property = data.hosts.find(h => String(h.property_id) === String(editPropertyId));
      if (property) {
        setPostRole('host');
        setPostPropertyId(property.property_id || '');
        setPostText(property.collab_opportunity || property.description || '');

        const details = property.collab_details || {};
        setDatesType(details.dates?.type || 'flexible');
        setDateStart(details.dates?.start || '');
        setDateEnd(details.dates?.end || '');
        setCheckInDate(details.dates?.checkIn || '');
        setCheckOutDate(details.dates?.checkOut || '');
        setCompensationType(details.compensation?.type || '');
        setBudgetMin(details.compensation?.budgetMin || '');
        setBudgetMax(details.compensation?.budgetMax || '');
        setDeliverablesType(details.deliverables?.type || 'flexible');
        setDeliverables(details.deliverables?.items || []);
        setGuestCapacity(details.stay?.guestCapacity || '');
        setPrimaryGoal(details.primaryGoal || '');
        setAdditionalNotes(details.additionalNotes || '');
        setLocation(details.location || '');

        const expiresAt = property.collab_post_expires_at;
        if (expiresAt) {
          setPostExpirationType('date');
          setPostExpirationDate(new Date(expiresAt).toISOString().split('T')[0]);
        } else {
          setPostExpirationType('indefinitely');
          setPostExpirationDate('');
        }

        setIsEditMode(true);
        setPostModalOpen(true);
      }
    } else if (editCreatorId) {
      const creator = data.creators.find(c => String(c.profile_id) === String(editCreatorId));
      if (creator) {
        setPostRole('creator');
        setPostPropertyId('');
        setPostText(creator.collab_request || '');

        const details = creator.collab_details || {};
        setDatesType(details.dates?.type || 'flexible');
        setDateStart(details.dates?.start || '');
        setDateEnd(details.dates?.end || '');
        setCheckInDate(details.dates?.checkIn || '');
        setCheckOutDate(details.dates?.checkOut || '');
        setCompensationType(details.compensation?.type || '');
        setBudgetMin(details.compensation?.budgetMin || '');
        setBudgetMax(details.compensation?.budgetMax || '');
        setDeliverablesType(details.deliverables?.type || 'flexible');
        setDeliverables(details.deliverables?.items || []);
        setGuestCapacity(details.stay?.guestCapacity || '');
        setPrimaryGoal(details.primaryGoal || '');
        setAdditionalNotes(details.additionalNotes || '');
        setLocation(details.location || '');

        const expiresAt = creator.collab_post_expires_at;
        if (expiresAt) {
          setPostExpirationType('date');
          setPostExpirationDate(new Date(expiresAt).toISOString().split('T')[0]);
        } else {
          setPostExpirationType('indefinitely');
          setPostExpirationDate('');
        }

        setIsEditMode(true);
        setPostModalOpen(true);
      }
    }
  }, [data.hosts, data.creators]);

  // Load only this user's properties if they are a host
  useEffect(() => {
    const loadUserProperties = async () => {
      if (authorized !== true || !email || !userRoles.includes('host')) return;
      try {
        const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
        const res = await fetch(`${base}/api/properties/list?email=${encodeURIComponent(email)}`);
        const j = await res.json();
        if (res.ok && j.ok) {
          setUserProperties(j.properties || []);
        }
      } catch (_) {}
    };
    loadUserProperties();
  }, [authorized, email, userRoles]);

  // Auto-populate location when host selects a property
  useEffect(() => {
    if (postRole === 'host' && postPropertyId) {
      // Convert both to strings for comparison (handles UUID vs string)
      const selectedProperty = userProperties.find(p => String(p.id) === String(postPropertyId));
      if (selectedProperty) {
        const parts = [];
        if (selectedProperty.location_city) parts.push(selectedProperty.location_city);
        if (selectedProperty.location_state) parts.push(selectedProperty.location_state);
        if (selectedProperty.location_country && selectedProperty.location_country !== 'United States') {
          parts.push(selectedProperty.location_country);
        }
        const locationString = parts.join(', ') || '';
        setLocation(locationString);
      } else {
        setLocation('');
      }
    } else if (postRole === 'host' && !postPropertyId) {
      // Clear location when no property is selected
      setLocation('');
    }
  }, [postPropertyId, postRole, userProperties]);

  // Realtime search with debouncing
  useEffect(() => {
    // Skip if not authorized or searchInput hasn't changed
    if (authorized !== true || !email || searchInput === searchTerm) return;
    
    const delayDebounceFn = setTimeout(() => {
      setSearchTerm(searchInput);
      fetchCollabBoard(searchInput);
    }, 400); // 400ms delay

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput, authorized, email]);

  if (authorized === null) {
    return <InfoPage belowHeader={<div style={{ textAlign: 'center', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", color: '#0f084b' }}>Checking access…</div>} disableStamps tallOverride={false} customMinHeight={600} showInlineLivePulse />;
  }

  if (authorized === 'basic' || membershipTier === 'basic') {
    const userType = userRoles.includes('host') ? 'host' : (userRoles.includes('traveler') ? 'traveler' : 'host');
    return (
      <InfoPage below={
        <BlurredGate userType={userType}>
          <div className="collab-board-page">
            <div className="collab-board-description"><p>A place exclusively for content creators and hosts to connect and collaborate.</p></div>
            <div className="collab-board-container">
              <div className="collab-board-section">
                <div className="collab-board-section-header"><div className="collab-board-section-title">Our Hosts</div></div>
                <div className="collab-board-carousel-grid" style={{ minHeight: 300 }} />
              </div>
              <div className="collab-board-section">
                <div className="collab-board-section-header"><div className="collab-board-section-title">Our Creators</div></div>
                <div className="collab-board-carousel-grid" style={{ minHeight: 300 }} />
              </div>
            </div>
          </div>
        </BlurredGate>
      } disableStamps tallOverride={false} customMinHeight={720} showInlineLivePulse />
    );
  }

  // No additional header; brand/title already shown in PostcardHero
  
  // Navigation handlers
  const handleHostPrev = () => {
    if (hostStartIndex > 0) {
      setHostDirection(-1);
      setHostStartIndex(hostStartIndex - 1);
    }
  };

  const handleHostNext = () => {
    if (hostStartIndex + 5 < data.hosts.length) {
      setHostDirection(1);
      setHostStartIndex(hostStartIndex + 1);
    }
  };

  const handleCreatorPrev = () => {
    if (creatorStartIndex > 0) {
      setCreatorDirection(-1);
      setCreatorStartIndex(creatorStartIndex - 1);
    }
  };

  const handleCreatorNext = () => {
    if (creatorStartIndex + 5 < data.creators.length) {
      setCreatorDirection(1);
      setCreatorStartIndex(creatorStartIndex + 1);
    }
  };

  const below = (
    <div className="collab-board-page">
      {/* Add Collab Opportunity Button */}
      {(userRoles.includes('host') || userRoles.includes('creator')) && (
        <div className="collab-board-add-button">
          <button
            className="button"
            type="button"
            onClick={() => {
              const eligibleRoles = userRoles.filter(role => role === 'host' || role === 'creator');
              const role = eligibleRoles.includes(activePersona) ? activePersona : eligibleRoles[0];
              setPostRole(role);
              setPostText('');
              setPostPropertyId('');
              // Reset all enhanced fields
              setDatesType('flexible');
              setDateStart('');
              setDateEnd('');
              setCompensationType('');
              setBudgetMin('');
              setBudgetMax('');
              setDeliverablesType('flexible');
              setDeliverables([]);
              setGuestCapacity('');
              setCheckInDate('');
              setCheckOutDate('');
              setPrimaryGoal('');
              setAdditionalNotes('');
              setLocation('');
              // Reset edit mode for new post
              setIsEditMode(false);
              setPostExpirationType('indefinitely');
              setPostExpirationDate('');
              setPostModalOpen(true);
            }}
            style={{ cursor: 'pointer' }}
          >
            Add Collab Opportunity
          </button>
        </div>
      )}
      
      <div className="collab-board-description">
        <p>
          A place exclusively for content creators and hosts to connect and collaborate.
        </p>
      </div>
      
      {/* Search Bar - Realtime Search */}
      <div className="collab-board-search-container">
        <div className="collab-board-search-wrapper">
          <input
            type="text"
            placeholder="Search creators and hosts..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
            className="collab-board-search-input"
            style={{ paddingRight: searchInput ? '40px' : '16px' }}
            onFocus={(e) => e.target.style.borderColor = '#141416'}
            onBlur={(e) => e.target.style.borderColor = '#E6E8EC'}
          />
          {searchInput && (
            <button
              onClick={handleClearSearch}
              className="collab-board-search-clear"
            >
              ×
            </button>
          )}
        </div>
      </div>
      {searchTerm && (
        <div className="collab-board-search-info">
          <p>
            Searching for "{searchTerm}"
          </p>
        </div>
      )}
      
      <div className="collab-board-container">
        <div className="collab-board-section">
          <div className="collab-board-section-header">
            <div className="collab-board-section-title">Our Hosts</div>
            <div className="collab-board-hosts-controls">
              {MAP_ENABLED && (
                <div className="collab-board-view-toggle" role="tablist" aria-label="Hosts view">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={hostsView === 'grid'}
                    className={`collab-board-view-toggle-btn${hostsView === 'grid' ? ' active' : ''}`}
                    onClick={() => setHostsView('grid')}
                  >
                    Grid
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={hostsView === 'map'}
                    className={`collab-board-view-toggle-btn${hostsView === 'map' ? ' active' : ''}`}
                    onClick={() => setHostsView('map')}
                  >
                    Map
                  </button>
                </div>
              )}
              {hostsView === 'grid' && data.hosts.filter(property => property.property_image_url).length > 5 && (
                <button
                  type="button"
                  className="collab-board-view-all-btn"
                  aria-expanded={showAllHosts}
                  aria-controls="hosts-grid"
                  onClick={() => setShowAllHosts(!showAllHosts)}
                >
                  {showAllHosts ? 'Show Less' : 'View All'}
                </button>
              )}
            </div>
          </div>
          {hostsView === 'map' ? (
            <Suspense fallback={<div className="collab-board-map-loading">Loading map…</div>}>
              <CollabBoardMap
                hosts={data.hosts}
                creators={data.creators}
                onSelectHost={openHostModalFromProperty}
                onSelectCreator={openCreatorModalFromProfile}
                onClose={() => setHostsView('grid')}
              />
            </Suspense>
          ) : (
          <div className={`collab-board-carousel-container${showAllHosts ? ' view-all' : ''}`}>
            {!showAllHosts && (
              <AnimatePresence>
                <NavigationButton 
                  direction="left" 
                  onClick={handleHostPrev} 
                  show={data.hosts.length > 5 && hostStartIndex > 0}
                />
                <NavigationButton 
                  direction="right" 
                  onClick={handleHostNext} 
                  show={data.hosts.length > 5 && hostStartIndex + 5 < data.hosts.length}
                />
              </AnimatePresence>
            )}
            <AnimatePresence mode="wait">
              <motion.div
                key={showAllHosts ? 'all' : hostStartIndex}
                id="hosts-grid"
                initial={showAllHosts ? { opacity: 0.8 } : { x: hostDirection === -1 ? -30 : 30, opacity: 0.8 }}
                animate={{ x: 0, opacity: 1 }}
                exit={showAllHosts ? { opacity: 0.8 } : { x: hostDirection === -1 ? 30 : -30, opacity: 0.8 }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="collab-board-carousel-grid"
              >
              {(showAllHosts
                ? data.hosts.filter(property => property.property_image_url)
                : data.hosts.filter(property => property.property_image_url).slice(hostStartIndex, hostStartIndex + 5)
              ).map((property, i) => {
              const rawUrl = property.property_image_url;
              const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
              let resolved = rawUrl || '';
              if (resolved.startsWith('/')) {
                if (isLocal) {
                  resolved = resolved.startsWith('/uploads/') ? `http://localhost:4000${resolved}` : resolved;
                }
                // Don't fallback to founders.png - filter out properties without images
              }
              // Don't resolve to founders.png - only show properties with valid images
              if (!resolved) return null;
              
              // Apply Cloudinary optimization for card images (3:4 portrait aspect ratio)
              const optimizedImage = getCloudinaryUrl(resolved, {
                width: 450,
                height: 600,
                crop: 'fill',
                quality: 'auto',
                format: 'auto'
              });
              
              // Display property handle from property slug (fallback to property name)
              const slug = property.property_slug || property.slug;
              const nameFallback = property.property_name || property.name || 
                                    (() => {
                                      const parts = [];
                                      if (property.location_city) parts.push(property.location_city);
                                      if (property.location_state) parts.push(property.location_state);
                                      if (property.location_country && property.location_country !== 'United States') {
                                        parts.push(property.location_country);
                                      }
                                      return parts.join(', ') || 'Property';
                                    })();
              const propertyDisplay = slug ? `@${slug}` : nameFallback;
              const displayHandle = propertyDisplay; // retained for compatibility (unused)
              
              return (
                <Card 
                  key={`property-${property.property_id || i}`} 
                  img={optimizedImage} 
                  fallbackImg={resolved}
                  handle={propertyDisplay} 
                  likeCount={property.like_count || 0}
                  isLiked={property.is_liked || false}
                  propertyId={property.property_id}
                  collabDetails={property.collab_details}
                  collabStatus={property.collab_status}
                  introVideoUrl={property.property_intro_video_url || property.intro_video_url}
                  introVideoPosterUrl={property.property_intro_video_poster_url || property.intro_video_poster_url}
                  introVideoPublicId={property.property_intro_video_public_id || property.intro_video_public_id}
                  onLikeToggle={(liked, count) => handleLikeToggle(liked, count, null, property.property_id)}
                  onClick={() => {
                    setSelectedHost({ 
                      ...property, 
                      description: property.collab_opportunity || property.description,
                      image: resolved,
                      plan_id: property.plan_id,
                      plan_name: property.plan_name,
                      handle: property.handle,
                      user_id: property.user_id,
                      airbnb_url: property.airbnb_url,
                      vrbo_url: property.vrbo_url,
                      direct_booking_url: property.direct_booking_url,
                      founder: property.founder,
                      collab_details: property.collab_details,
                      collab_status: property.collab_status,
                      square_footage: property.square_footage
                    });
                    setModalOpen(true);
                  }}
                />
              );
              })}
            </motion.div>
            </AnimatePresence>
          </div>
          )}
        </div>
        <div className="collab-board-section">
          <div className="collab-board-section-header">
            <div className="collab-board-section-title">Our Creators</div>
            {data.creators.filter(c => c.avatar_asset_url || c.avatar_url).length > 5 && (
              <button
                type="button"
                className="collab-board-view-all-btn"
                aria-expanded={showAllCreators}
                aria-controls="creators-grid"
                onClick={() => setShowAllCreators(!showAllCreators)}
              >
                {showAllCreators ? 'Show Less' : 'View All'}
              </button>
            )}
          </div>
          <div className={`collab-board-carousel-container${showAllCreators ? ' view-all' : ''}`}>
            {!showAllCreators && (
              <AnimatePresence>
                <NavigationButton 
                  direction="left" 
                  onClick={handleCreatorPrev} 
                  show={data.creators.length > 5 && creatorStartIndex > 0}
                />
                <NavigationButton 
                  direction="right" 
                  onClick={handleCreatorNext} 
                  show={data.creators.length > 5 && creatorStartIndex + 5 < data.creators.length}
                />
              </AnimatePresence>
            )}
            <AnimatePresence mode="wait">
              <motion.div 
                key={showAllCreators ? 'all' : creatorStartIndex}
                id="creators-grid"
                initial={showAllCreators ? { opacity: 0.8 } : { x: creatorDirection === -1 ? -30 : 30, opacity: 0.8 }}
                animate={{ x: 0, opacity: 1 }}
                exit={showAllCreators ? { opacity: 0.8 } : { x: creatorDirection === -1 ? 30 : -30, opacity: 0.8 }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="collab-board-carousel-grid"
              >
              {(showAllCreators
                ? data.creators.filter(c => c.avatar_asset_url || c.avatar_url)
                : data.creators.filter(c => c.avatar_asset_url || c.avatar_url).slice(creatorStartIndex, creatorStartIndex + 5)
              ).map((c, i) => {
              const rawUrl = c.avatar_asset_url || c.avatar_url;
              const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
              let resolved = rawUrl || '';
              if (resolved.startsWith('/')) {
                if (isLocal) {
                  resolved = resolved.startsWith('/uploads/') ? `http://localhost:4000${resolved}` : resolved;
                }
                // Don't fallback to about-us.png - filter out creators without images
              }
              // Don't resolve to about-us.png - only show creators with valid images
              if (!resolved) return null;
              
              // Apply Cloudinary optimization for card images (3:4 portrait aspect ratio)
              const optimizedImage = getCloudinaryUrl(resolved, {
                width: 450,
                height: 600,
                crop: 'fill',
                quality: 'auto',
                format: 'auto'
              });
              
              // Use handle, or extract Instagram handle as fallback
              const instagramHandle = extractInstagramHandle(c.instagram_url);
              const displayHandle = c.handle ? fmtHandle(c.handle) : (instagramHandle || (c.first_name ? c.first_name : '@creator'));
              
              return (
                <Card 
                  key={`c-${c.profile_id || i}`} 
                  img={optimizedImage} 
                  fallbackImg={resolved}
                  handle={displayHandle} 
                  likeCount={c.like_count || 0}
                  isLiked={c.is_liked || false}
                  profileId={c.profile_id}
                  collabDetails={c.collab_details}
                  collabStatus={c.collab_status}
                  introVideoUrl={c.intro_video_url}
                  introVideoPosterUrl={c.intro_video_poster_url}
                  introVideoPublicId={c.intro_video_public_id}
                  onLikeToggle={(liked, count) => handleLikeToggle(liked, count, c.profile_id, null)}
                  onClick={c.collab_request ? () => {
                    setSelectedHost({ 
                      ...c,
                      property_name: displayHandle,
                      description: c.collab_request,
                      image: resolved,
                      plan_id: c.plan_id,
                      plan_name: c.plan_name,
                      handle: c.handle,
                      user_id: c.user_id,
                      profile_id: c.profile_id,
                      founder: c.founder,
                      collab_details: c.collab_details,
                      collab_status: c.collab_status
                    });
                    setModalOpen(true);
                  } : null}
                />
              );
              })}
            </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <InfoPage below={below} disableStamps tallOverride={false} customMinHeight={720} showInlineLivePulse />
      <Modal 
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedHost(null);
          setFocusCommentId(null);
        }}
        title={
          selectedHost?.property_name || 
          (selectedHost?.brand_name ? 
            `${selectedHost.brand_name} - ${selectedHost.location_city || 'Property'}` : 
            'Property')
        }
        content={selectedHost?.description || selectedHost?.collab_opportunity}
        image={selectedHost?.image}
        planId={selectedHost?.plan_id}
        planName={selectedHost?.plan_name}
        messageUserId={selectedHost?.user_id}
        propertyId={selectedHost?.property_id || selectedHost?.id}
        propertySlug={selectedHost?.property_slug || selectedHost?.slug}
        profileId={selectedHost?.profile_id}
        host_profile_id={selectedHost?.host_profile_id}
        isOwner={selectedHost?.user_id && currentUserId && String(selectedHost.user_id) === String(currentUserId)}
        handle={selectedHost?.handle}
        airbnbUrl={selectedHost?.airbnb_url}
        vrboUrl={selectedHost?.vrbo_url}
        directBookingUrl={selectedHost?.direct_booking_url}
        founder={selectedHost?.founder}
        collabDetails={selectedHost?.collab_details}
        collabStatus={selectedHost?.collab_status}
        squareFootage={selectedHost?.square_footage}
        locationCity={selectedHost?.location_city}
        locationState={selectedHost?.location_state}
        locationCountry={selectedHost?.location_country}
        focusCommentId={focusCommentId}
        onStatusChange={handleStatusChange}
        onApply={(ctx) => {
          // Close the details modal first so ApplyModal owns the foreground
          // scroll-lock (mirrors the SummitPromoModal pattern).
          setModalOpen(false);
          setApplyContext(ctx);
        }}
        onEdit={() => {
          // Close details modal
          setModalOpen(false);
          
          // Determine if this is a host (property) or creator opportunity
          const isHostProperty = Boolean(selectedHost?.property_id);
          const role = isHostProperty ? 'host' : 'creator';
          
          // Prefill form fields from selectedHost
          setPostRole(role);
          setPostPropertyId(isHostProperty ? (selectedHost.property_id || '') : '');
          setPostText(selectedHost?.description || selectedHost?.collab_opportunity || selectedHost?.collab_request || '');
          
          // Prefill enhanced fields from collab_details
          const details = selectedHost?.collab_details || {};
          
          // Dates
          setDatesType(details.dates?.type || 'flexible');
          setDateStart(details.dates?.start || '');
          setDateEnd(details.dates?.end || '');
          setCheckInDate(details.dates?.checkIn || '');
          setCheckOutDate(details.dates?.checkOut || '');
          
          // Compensation
          setCompensationType(details.compensation?.type || '');
          setBudgetMin(details.compensation?.budgetMin || '');
          setBudgetMax(details.compensation?.budgetMax || '');
          
          // Deliverables
          setDeliverablesType(details.deliverables?.type || 'flexible');
          setDeliverables(details.deliverables?.items || []);
          
          // Stay
          setGuestCapacity(details.stay?.guestCapacity || '');
          
          // Other fields
          setPrimaryGoal(details.primaryGoal || '');
          setAdditionalNotes(details.additionalNotes || '');
          setLocation(details.location || '');
          
          // Pre-fill expiration date
          const expiresAt = selectedHost?.collab_post_expires_at;
          if (expiresAt) {
            setPostExpirationType('date');
            setPostExpirationDate(new Date(expiresAt).toISOString().split('T')[0]);
          } else {
            setPostExpirationType('indefinitely');
            setPostExpirationDate('');
          }
          
          // Set edit mode and open post modal
          setIsEditMode(true);
          setPostModalOpen(true);
        }}
        onDelete={async () => {
          try {
            const token = getAuthToken();
            if (!token) {
              alert('Please log in to delete this opportunity');
              return;
            }
            
            const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const type = selectedHost?.property_id ? 'property' : 'profile';
            const params = new URLSearchParams();
            params.set('type', type);
            if (type === 'property') {
              params.set('propertyId', selectedHost.property_id || selectedHost.id);
            } else {
              // For creators, use profile_id; for hosts, use host_profile_id
              params.set('profileId', selectedHost.profile_id || selectedHost.host_profile_id || selectedHost.id);
            }
            
            const res = await authFetch(`${base}/api/collab/delete?${params.toString()}`, {
              method: 'DELETE'
            });
            
            const data = await res.json();
            if (res.ok && data.ok) {
              setModalOpen(false);
              setSelectedHost(null);
              // Refresh the board
              await fetchCollabBoard(searchTerm || '');
            } else {
              alert('Failed to delete collaboration opportunity');
            }
          } catch (err) {
            // Don't log auth errors since authFetch handles logout
            if (!err.message?.includes('Unauthorized') && !err.message?.includes('expired')) {
              console.error('Delete failed:', err);
              alert('Failed to delete collaboration opportunity');
            }
          }
        }}
      />
      {/* Post Collab Modal */}
      {postModalOpen && (
        <div 
          className="collab-post-modal-overlay"
          onClick={() => {
            setPostModalOpen(false);
            setIsEditMode(false);
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="collab-post-modal-content collab-post-modal-enhanced"
          >
            <h3>{isEditMode ? 'Edit Collab Opportunity' : 'Post a Collab Opportunity'}</h3>
            <div className="collab-post-scrollable">
              {postRole === 'host' && (
                <div className="collab-post-field">
                  <label>Which property is this collab opportunity available for?</label>
                  <select value={postPropertyId} onChange={(e) => setPostPropertyId(e.target.value)}>
                    <option value="">Select a property…</option>
                    {userProperties.map((p) => (
                      <option key={p.id} value={p.id}>{p.name || 'Property'}{(() => {
                        const parts = [];
                        if (p.location_city) parts.push(p.location_city);
                        if (p.location_state) parts.push(p.location_state);
                        if (p.location_country && p.location_country !== 'United States') {
                          parts.push(p.location_country);
                        }
                        return parts.length ? ` — ${parts.join(', ')}` : '';
                      })()}</option>
                    ))}
                  </select>
                  {userProperties.length === 0 && (
                    <div className="collab-post-no-properties">
                      You have no properties yet. Add one from your dashboard first.
                    </div>
                  )}
              </div>
            )}

              {/* Main Description/Overview */}
              <div className="collab-post-field">
                <label>Description / Overview</label>
                <textarea
                  value={postText}
                  onChange={(e) => setPostText(e.target.value)}
                  placeholder={postRole === 'host' ? 'Tell creators about this opportunity...' : "Tell hosts what you're looking for..."}
                  rows={4}
                />
              </div>

              {/* Location — hosts only */}
              {postRole === 'host' && (
              <div className="collab-post-field">
                <label>Location</label>
                {postRole === 'host' ? (
                  <input
                    type="text"
                    value={location}
                    readOnly
                    placeholder={postPropertyId ? "Location will be populated from property..." : "Select a property first..."}
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                ) : (
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="City, State or City, Country"
                  />
                )}
              </div>
              )}

              {/* Dates of Collab */}
              <div className="collab-post-field">
                <label>Dates of Collab</label>
                <div className="collab-post-radio-group">
                  <label className="collab-post-radio">
                    <input type="radio" name="datesType" value="flexible" checked={datesType === 'flexible'} onChange={(e) => setDatesType(e.target.value)} />
                    <span>Flexible</span>
                  </label>
                  <label className="collab-post-radio">
                    <input type="radio" name="datesType" value="specific" checked={datesType === 'specific'} onChange={(e) => setDatesType(e.target.value)} />
                    <span>Specific</span>
                  </label>
                </div>
                {datesType === 'specific' && (
                  <div className="collab-post-date-range">
                    <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} placeholder="Start date" />
                    <span>to</span>
                    <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} placeholder="End date" />
                  </div>
                )}
              </div>

              {/* Compensation Type */}
              <div className="collab-post-field">
                <label>Compensation Type</label>
                <select value={compensationType} onChange={(e) => setCompensationType(e.target.value)}>
                  <option value="">Select compensation type…</option>
                  <option value="paid_partnership">Paid Partnership</option>
                  <option value="hosted_stay">Hosted Stay</option>
                  <option value="negotiable">Negotiable</option>
                </select>
                {compensationType === 'paid_partnership' && (
                  <div className="collab-post-budget-range">
                    <input type="number" value={budgetMin} onChange={(e) => setBudgetMin(e.target.value)} placeholder="Min $" />
                    <span>to</span>
                    <input type="number" value={budgetMax} onChange={(e) => setBudgetMax(e.target.value)} placeholder="Max $" />
                  </div>
                )}
              </div>

              {/* Deliverables */}
              <div className="collab-post-field">
                <label>Deliverables</label>
                <div className="collab-post-radio-group">
                  <label className="collab-post-radio">
                    <input type="radio" name="deliverablesType" value="flexible" checked={deliverablesType === 'flexible'} onChange={(e) => setDeliverablesType(e.target.value)} />
                    <span>Flexible</span>
                  </label>
                  <label className="collab-post-radio">
                    <input type="radio" name="deliverablesType" value="specific" checked={deliverablesType === 'specific'} onChange={(e) => setDeliverablesType(e.target.value)} />
                    <span>Specific</span>
                  </label>
                </div>
                {deliverablesType === 'specific' && (
                  <div className="collab-post-checkboxes">
                    {['In-feed Reel', 'In-feed Post', 'Stories', 'Still images', 'Drone footage', 'B-roll', 'Amenity highlight'].map((deliverable) => (
                      <label key={deliverable} className="collab-post-checkbox">
                        <input
                          type="checkbox"
                          checked={deliverables.includes(deliverable)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setDeliverables([...deliverables, deliverable]);
                            } else {
                              setDeliverables(deliverables.filter(d => d !== deliverable));
                            }
                          }}
                        />
                        <span>{deliverable}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Guest Capacity — hosts only */}
              {postRole === 'host' && (
              <div className="collab-post-field">
                <label>Guest Capacity (Max)</label>
                <input type="number" value={guestCapacity} onChange={(e) => setGuestCapacity(e.target.value)} placeholder="Max guests" min="1" />
              </div>
              )}

              {/* Check-in/Check-out Dates (if specific dates selected) */}
              {datesType === 'specific' && (
                <>
                  <div className="collab-post-field">
                    <label>Check-in Date</label>
                    <input type="date" value={checkInDate} onChange={(e) => setCheckInDate(e.target.value)} />
                  </div>
                  <div className="collab-post-field">
                    <label>Check-out Date</label>
                    <input type="date" value={checkOutDate} onChange={(e) => setCheckOutDate(e.target.value)} />
                  </div>
                </>
              )}

              {/* Primary Goal — hosts only */}
              {postRole === 'host' && (
              <div className="collab-post-field">
                <label>What is your primary goal?</label>
                <select value={primaryGoal} onChange={(e) => setPrimaryGoal(e.target.value)}>
                  <option value="">Select goal…</option>
                  <option value="increase_bookings">To increase bookings</option>
                  <option value="increase_following">To increase social media following</option>
                  <option value="update_photos">To update listing photos</option>
                </select>
              </div>
              )}

              {/* Additional Notes */}
              <div className="collab-post-field">
                <label>Additional Notes</label>
                <textarea
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  placeholder="Any additional information..."
                  rows={4}
                />
              </div>

              {/* Post Expiration */}
              <div className="collab-post-field">
                <label>How long would you like to keep this post on the Board?</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="collabPostExpiration"
                      checked={postExpirationType === 'date'}
                      onChange={() => setPostExpirationType('date')}
                    />
                    Specific Expiration Date
                  </label>
                  {postExpirationType === 'date' && (
                    <input
                      type="date"
                      value={postExpirationDate}
                      onChange={(e) => setPostExpirationDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      style={{ marginLeft: '24px', maxWidth: '200px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' }}
                    />
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="collabPostExpiration"
                      checked={postExpirationType === 'indefinitely'}
                      onChange={() => { setPostExpirationType('indefinitely'); setPostExpirationDate(''); }}
                    />
                    Indefinitely
                  </label>
                </div>
              </div>
            </div>

            <div className="collab-post-actions">
              <button
                type="button"
                className="button-stroke"
                onClick={() => {
                  setPostModalOpen(false);
                  setIsEditMode(false);
                  setPostExpirationType('indefinitely');
                  setPostExpirationDate('');
                }}
                style={{ cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button"
                onClick={async () => {
                  try {
                    const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
                    
                    // Build structured collab details
                    const collabDetails = {
                      dates: {
                        type: datesType,
                        start: datesType === 'specific' ? dateStart : null,
                        end: datesType === 'specific' ? dateEnd : null,
                        checkIn: datesType === 'specific' ? checkInDate : null,
                        checkOut: datesType === 'specific' ? checkOutDate : null,
                      },
                      compensation: {
                        type: compensationType,
                        budgetMin: compensationType === 'paid_partnership' ? budgetMin : null,
                        budgetMax: compensationType === 'paid_partnership' ? budgetMax : null,
                      },
                      deliverables: {
                        type: deliverablesType,
                        items: deliverablesType === 'specific' ? deliverables : null,
                      },
                      stay: {
                        guestCapacity: guestCapacity || null,
                      },
                      primaryGoal: primaryGoal || null,
                      location: location || null,
                      additionalNotes: additionalNotes || null,
                    };

                    const payload = { 
                      email, 
                      role: postRole, 
                      text: postText,
                      collab_details: collabDetails,
                      expiresAt: postExpirationType === 'date' && postExpirationDate ? postExpirationDate : null
                    };
                    
                    if (postRole === 'host') {
                      if (!postPropertyId) { alert('Please select one of your properties'); return; }
                      payload.property_id = postPropertyId;
                    }
                    
                    const res = await fetch(`${base}/api/collab/post`, { 
                      method: 'POST', 
                      headers: { 'Content-Type': 'application/json' }, 
                      body: JSON.stringify(payload) 
                    });
                    
                    const j = await res.json();
                    if (!res.ok || !j.ok) throw new Error('post_failed');
                    setPostModalOpen(false);
                    setIsEditMode(false);
                    // Refresh the board so the new item shows first
                    await fetchCollabBoard(searchTerm || '');
                  } catch (_) {
                    alert('Unable to post collab. Please try again.');
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                {isEditMode ? 'Save Changes' : 'Post Collab'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Application gate: replaces the legacy "navigate to /messages and
          open a DM" flow. No Stream channel is created here; the host
          approves first from their /collabs Pending tab. */}
      <ApplyModal
        isOpen={!!applyContext}
        recipientUserId={applyContext?.recipientUserId}
        recipientHandle={applyContext?.recipientHandle}
        title={applyContext?.title}
        targetKind={applyContext?.targetKind}
        targetId={applyContext?.targetId}
        collabDetails={applyContext?.collabDetails}
        onClose={() => setApplyContext(null)}
        onApplied={() => {
          // Defer the close briefly so the "Application sent" success
          // state has a chance to render before unmounting. The user can
          // also just hit Done in the modal.
        }}
      />
    </>
  );
};

export default CollabBoard;


